/**
 * Chat Hook
 * React hook for streaming chat with thinking steps via SSE
 * Messages are persisted to the server for cross-device sync
 */

import { useState, useCallback, useRef, useEffect } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useStreamActivity } from './useStreamActivity'
import { useChatGreeting } from './useChatGreeting'
import { useSendQueue, isDuplicateContentSend } from './useSendQueue'
import { loadSessionMessages } from './useSessionMessageLoader'
import { reloadMessagesOnForeground } from './useForegroundReload'
// Per-turn context factory + the stable dependency-bundle type, and the two
// deferred cores (PHASE 2 decomposition). The SSE loop + finalize +
// error/abort recovery (core A) live in runChatStream; the ui-action
// report.*/html.* switch (core B) is invoked from there via applyUiDirective.
// sendMessage builds `ctx` (createTurnContext) + `deps` and calls runChatStream.
import { createTurnContext } from './useChatStream.types'
import type { ChatStreamDeps } from './useChatStream.types'
import { runChatStream } from './runChatStream'
// isModuleNavigationMessage gates the nav-noise pair-replacement in sendMessage.
import { isModuleNavigationMessage } from '../utils/chat-errors'
// appendQuickActions is the pure dedupe-by-label reducer behind addQuickActions
// (the imperative chip-append API used by the client-side CSV import, #2014).
import { appendQuickActions } from '../utils/import-followups'
// Re-export the test-locked + export-surface helpers so importer paths stay
// byte-identical (tests import sanitizeHiddenDirectives, endsWithQuestion,
// handleHtmlPatchDirective, hasExplicitRebuildIntent from './useChat'; findSectionRange
// preserves the historical export surface).
export {
  sanitizeHiddenDirectives,
  endsWithQuestion,
  handleHtmlPatchDirective,
  hasExplicitRebuildIntent,
  findSectionRange,
} from '../utils/hidden-directives'
import { useSession } from '@/features/auth/hooks/useSession'
import { useConversation } from '@/app/providers/ConversationContext'
import type { ChatRequest, ChatUIAction } from '@/lib/api/endpoints/chat'
import {
  saveConversationChatMessage,
  clearChatSession,
} from '@/lib/api/endpoints/chat'
import { isNative } from '@/lib/capacitor'
import { resolveWorkspaceId } from '@/lib/workspace-routing'
// Public type surface lives in useChat.types.ts (PR6). Imported for internal
// use and re-exported below so every importer's path + symbol identity stay
// byte-identical (ChatBox imports QuickAction + ChatAttachment from this path;
// tests import from both './useChat' and '@features/chat/hooks/useChat').
import type {
  ChatAttachment,
  ChatMessage,
  QuickAction,
  UseChatOptions,
  UseChatReturn,
} from './useChat.types'
export type {
  ChatAttachment,
  ChatMessage,
  StreamActivityStep,
  QuickAction,
  UseChatOptions,
  UseChatReturn,
} from './useChat.types'

// #2458: id-keyed pending-create registry. The targeted list-query invalidation
// (scheduleObjectListInvalidation) moved with the SSE loop into runChatStream;
// sendMessage still constructs the registry and passes it via deps.
import { createPendingCreateRegistry } from '@/features/chat/utils/pendingCreateRegistry'
import { isOnboardingComplete } from '@xavior/contracts'
/**
 * Hook for managing chat conversations with SSE streaming
 * Messages are persisted to the server for cross-device sync
 */
export function useChat(options: UseChatOptions = {}): UseChatReturn {
  const {
    sessionId,
    mode,
    model,
    systemPrompt,
    allowedTools,
    activeModuleId,
    activeModuleObjectType,
    activeModuleLabel,
    activePageContext,
    activePageContextRef: externalActivePageContextRef,
    activeDriveFolder,
    activeDriveFolderRef: externalActiveDriveFolderRef,
    activeObjectContext,
    onMessage,
    onQuickAck,
    onToolActivity,
    onObjectCreated,
    onStreamTextChunk,
    onStreamEnd,
    onThinking,
    onError,
    onComplete,
    onUiActions,
  } = options
  const { token, guestId, profile, selectedContainer } = useSession()
  const profileRef = useRef(profile)
  profileRef.current = profile
  const { lastCreatedConversationId, clearLastCreatedConversationId } = useConversation()
  // Mirror context values into refs so the sessionId effect can read the
  // latest values without declaring them as deps (which would re-fire the
  // effect on every new-conversation create and cause wasteful GETs for the
  // previous conversation).
  const lastCreatedConversationIdRef = useRef<string | null>(lastCreatedConversationId)
  lastCreatedConversationIdRef.current = lastCreatedConversationId
  const clearLastCreatedConversationIdRef = useRef(clearLastCreatedConversationId)
  clearLastCreatedConversationIdRef.current = clearLastCreatedConversationId
  const workspaceId = resolveWorkspaceId(selectedContainer)
  const queryClient = useQueryClient()

  const [messages, setMessages] = useState<ChatMessage[]>([])
  const messagesRef = useRef<ChatMessage[]>([])
  // Keep ref in sync for use in sendMessage callback without stale closure
  messagesRef.current = messages
  const [thinking, setThinking] = useState<string | null>(null)
  // Stream-activity state machine (timeline state + ref + push/pushThinking/clear)
  // extracted verbatim into useStreamActivity (Stage 3). The ref + setter are
  // returned so the session-switch reset sites and the sendMessage read site
  // below keep working byte-identically.
  const {
    streamActivity,
    streamActivityRef,
    setStreamActivity,
    pushActivity,
    pushThinkingStatus,
    clearActivity,
  } = useStreamActivity()
  const [isStreaming, setIsStreaming] = useState(false)
  // Start loading if we have a sessionId (prevents greeting race condition)
  const [isLoading, setIsLoading] = useState(!!sessionId)
  const [error, setError] = useState<Error | null>(null)
  const [streamingMessage, setStreamingMessage] = useState<ChatMessage | null>(null)
  const [elapsedSeconds, setElapsedSeconds] = useState(0)
  const elapsedTimerRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const [pendingQuickActions, setPendingQuickActions] = useState<QuickAction[]>([])
  // Transient retry bubble (layer 3 of "No response" recovery).
  //
  // When a stream terminates with no chunks and no tool work at all,
  // we don't persist a literal "No response" string — we surface a
  // throwaway in-memory bubble with a retry button instead. It lives
  // only until the next user send (or an explicit dismiss) and is
  // never written to chat_messages, so refresh clears it.
  const [retryBubble, setRetryBubble] = useState<ChatMessage | null>(null)

  // Focused sections state — populated by PageRenderer inspector via the
  // `xavior:edit-sections-changed` CustomEvent. Forwarded to the agent on
  // every send so it knows which parts of the open page to edit. Cleared on
  // successful send or when inspect mode is turned off.
  const [focusedSections, setFocusedSections] = useState<
    Array<{ sectionId: string; label: string; selector?: string }>
  >([])
  const focusedSectionsRef = useRef<
    Array<{ sectionId: string; label: string; selector?: string }>
  >([])
  focusedSectionsRef.current = focusedSections

  useEffect(() => {
    const handleSections = (e: Event) => {
      const detail = (e as CustomEvent).detail
      if (Array.isArray(detail?.sections)) {
        setFocusedSections(
          detail.sections as Array<{ sectionId: string; label: string; selector?: string }>,
        )
      }
    }
    const handleInspectOff = () => setFocusedSections([])
    window.addEventListener('xavior:edit-sections-changed', handleSections)
    window.addEventListener('xavior:inspect-off', handleInspectOff)
    return () => {
      window.removeEventListener('xavior:edit-sections-changed', handleSections)
      window.removeEventListener('xavior:inspect-off', handleInspectOff)
    }
  }, [])

  // Mirror activePageContext prop into a ref so the sendMessage closure always
  // reads the freshest value. Without this, a rapid send fired during a React
  // render transition (tab switch, page-create race where the event dispatches
  // after the send callback is memoized) would capture a stale prop — causing
  // activePageContext to land as undefined in the outbound ChatRequest even
  // though a page is open.
  //
  // When the caller provides activePageContextRef (i.e. an external ref that
  // is updated SYNCHRONOUSLY in the CustomEvent handler before React batches
  // the state update), we use that directly. This closes the remaining race:
  // event fires → ref updated synchronously → user hits Enter → sendMessage
  // reads the external ref and sees the latest value even before re-render.
  const internalActivePageContextRef = useRef<typeof activePageContext>(activePageContext)
  internalActivePageContextRef.current = activePageContext
  const activePageContextRef = externalActivePageContextRef ?? internalActivePageContextRef
  const internalActiveDriveFolderRef = useRef<typeof activeDriveFolder>(activeDriveFolder)
  internalActiveDriveFolderRef.current = activeDriveFolder
  const activeDriveFolderRef = externalActiveDriveFolderRef ?? internalActiveDriveFolderRef
  const activeObjectContextRef = useRef<typeof activeObjectContext>(activeObjectContext)
  activeObjectContextRef.current = activeObjectContext

  // Track if greeting has been cancelled (user sent a message while greeting was streaming)
  const greetingCancelledRef = useRef(false)
  // Synchronous guard: prevents concurrent greeting calls (isStreaming is async/stale)
  const greetingInProgressRef = useRef(false)
  // Time-based dedup: tracks the last greeting request timestamp per conversation ID.
  // Prevents duplicate /chat requests for the same conversation within 5 seconds,
  // even if greetingInProgressRef has been reset in the finally block.
  const greetingLastRequestedRef = useRef<Map<string, number>>(new Map())
  // Separate abort controller for greeting requests
  const greetingAbortControllerRef = useRef<AbortController | null>(null)
  // Always points at the conversation currently shown in UI
  const currentSessionIdRef = useRef<string | undefined>(sessionId)
  currentSessionIdRef.current = sessionId
  const previousSessionIdRef = useRef<string | undefined>(sessionId)
  // Tracks a just-created conversation whose greeting we're letting fire
  // optimistically; prevents the server GET from wiping the greeting before
  // the server has persisted it. Also survives StrictMode's synthetic remount.
  const bypassedNewConversationIdRef = useRef<string | null>(null)

  // Synchronous derived-state-from-props: set isLoading = true during render when
  // sessionId changes. This prevents the greeting effect in ChatBox from seeing a
  // stale isLoading = false on the first render after a conversation switch, which
  // would cause it to fire before the message fetch completes — only for the greeting
  // to be wiped by the fetch result. (#1583)
  const renderedSessionIdRef = useRef<string | undefined>(sessionId)
  if (renderedSessionIdRef.current !== sessionId) {
    renderedSessionIdRef.current = sessionId
    if (sessionId && !sessionId.startsWith('conversation-')) {
      // React allows setState during render for derived state.
      // This triggers an immediate re-render before effects/paint.
      setIsLoading(true)
    }
  }

  // Load messages from server when sessionId changes (conversation switch).
  // Body extracted to loadSessionMessages (issue #3280) — the useEffect shell
  // and its [sessionId, token] dep array stay here byte-identically so React
  // hook order and re-fire timing are unchanged.
  useEffect(() => {
    loadSessionMessages({
      sessionId,
      token,
      previousSessionIdRef,
      greetingAbortControllerRef,
      greetingCancelledRef,
      greetingInProgressRef,
      bypassedNewConversationIdRef,
      lastCreatedConversationIdRef,
      clearLastCreatedConversationIdRef,
      currentSessionIdRef,
      streamActivityRef,
      setMessages,
      setThinking,
      setStreamActivity,
      setIsStreaming,
      setIsLoading,
      setError,
      setStreamingMessage,
      setPendingQuickActions,
    })
  }, [sessionId, token])

  // Keep track of abort controller to cancel streaming
  const abortControllerRef = useRef<AbortController | null>(null)
  // Track last SSE event timestamp to detect dead vs live streams on visibility change.
  // Initialized to Date.now() so a stream that hasn't emitted its first event yet
  // isn't incorrectly treated as stale (Date.now() - 0 would be a huge number).
  const lastStreamEventAtRef = useRef<number>(Date.now())
  // Batching refs for smooth streaming
  const streamingContentRef = useRef<string>('')
  const rafIdRef = useRef<number | null>(null)

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }
      if (rafIdRef.current) {
        cancelAnimationFrame(rafIdRef.current)
      }
      if (elapsedTimerRef.current) {
        clearInterval(elapsedTimerRef.current)
      }
    }
  }, [])

  // Start/stop elapsed timer when streaming state changes
  useEffect(() => {
    if (isStreaming) {
      // Reset and start timer
      setElapsedSeconds(0)
      elapsedTimerRef.current = setInterval(() => {
        setElapsedSeconds(prev => prev + 1)
      }, 1000)
    } else {
      // Stop timer
      if (elapsedTimerRef.current) {
        clearInterval(elapsedTimerRef.current)
        elapsedTimerRef.current = null
      }
    }

    return () => {
      if (elapsedTimerRef.current) {
        clearInterval(elapsedTimerRef.current)
        elapsedTimerRef.current = null
      }
    }
  }, [isStreaming])

  // When the app returns to the foreground after being backgrounded, reload messages
  // from the server. Mobile OSes kill SSE/fetch connections when backgrounded, so
  // the stream dies silently. On return, we check if the server finished the response.
  // On desktop, the stream usually survives tab-switching so we only abort if the
  // stream has been silent long enough to be considered dead.
  // Body extracted to reloadMessagesOnForeground (issue #3280) — the useEffect
  // shell and its [token, clearActivity] dep array stay here byte-identically.
  // The helper sets up the visibilitychange listener and returns the matching
  // cleanup, so re-fire timing and unmount cleanup are unchanged.
  useEffect(() => {
    return reloadMessagesOnForeground({
      token,
      abortControllerRef,
      currentSessionIdRef,
      lastStreamEventAtRef,
      elapsedTimerRef,
      setMessages,
      setThinking,
      setIsStreaming,
      setIsLoading,
      setStreamingMessage,
      clearActivity,
    })
  }, [token, clearActivity])

  // Send-queue refs (dedup + serialization) extracted to useSendQueue (Pass-2).
  // Called at the SAME body position the four useRefs occupied, so React hook
  // order is unchanged; the returned ref objects keep their identity at every
  // `.current` site below (sendMessage, finalizeAssistantMessage, the SSE loop,
  // retryLastMessage, the catch+finally).
  const {
    lastSendRef,
    sendInFlightRef,
    inFlightTurnRef,
    lastSentContentRef,
  } = useSendQueue()

  const sendMessage = useCallback(
    async (content: string, conversationId?: string, _objectId?: string, context?: any, options?: { hideUserMessage?: boolean; hideAssistantResponse?: boolean; voiceMode?: boolean; images?: Array<{ mediaType: string; data: string; filename: string }>; documents?: Array<{ mediaType: string; data: string; filename: string }>; attachments?: ChatAttachment[]; displayContent?: string; pastedAttachments?: Array<{ content: string; label: string; size: number }> }) => {
      if (!content.trim()) return

      // Sub-100ms dedup: block concurrent invocations from React StrictMode
      // or SSE reconnect replay (#1690). Released after the stream starts.
      if (sendInFlightRef.current) return
      sendInFlightRef.current = true

      // Reject duplicate sends of the same content within 60 seconds (#1655).
      const now = Date.now()
      if (isDuplicateContentSend(lastSendRef.current, content, now)) {
        sendInFlightRef.current = false
        return
      }
      lastSendRef.current = { content: content.trim(), at: now }

      // Store for retry on transient errors (#1696)
      lastSentContentRef.current = content.trim()

      const hideUserMessage = options?.hideUserMessage ?? false
      const hideAssistantResponse = options?.hideAssistantResponse ?? false
      const targetConversationId = conversationId ?? sessionId
      const isTargetConversationActive = () => currentSessionIdRef.current === targetConversationId

      // Clear previous error, quick actions, and any lingering retry bubble
      // from a previous failed send — starting a new turn resets recovery UI.
      if (isTargetConversationActive()) {
        setError(null)
        setPendingQuickActions([])
        setRetryBubble(null)
      }

      // Handle any greeting in progress or completed
      greetingCancelledRef.current = true
      // Abort greeting HTTP request to stop LLM generation
      if (greetingAbortControllerRef.current) {
        greetingAbortControllerRef.current.abort()
        greetingAbortControllerRef.current = null
      }
      if (isTargetConversationActive()) {
        setStreamingMessage((current) => {
          if (current && current.routing === 'greeting') {
            // If greeting is complete (has content and not streaming), preserve it in messages
            if (current.content && !(current as any).streaming) {
              setMessages((prev) => [current, ...prev]) // Prepend greeting before user message
            }
            // Clear from streaming state either way
            return null
          }
          return null
        })
        if (!hideAssistantResponse) {
          setIsLoading(true)
        }
      }

      // CRITICAL: capture the previous turn's promise AND register this turn's
      // promise SYNCHRONOUSLY before any await or state setter, so concurrent
      // sendMessage calls see a stable queue chain. If we did this after the
      // optimistic render, two rapid sends could both observe inFlightTurnRef
      // as null, skip the wait, and run in parallel — exactly what the queue
      // is meant to prevent.
      const previousTurn = inFlightTurnRef.current
      let resolveTurn: (() => void) | undefined
      const thisTurnPromise = new Promise<void>((r) => { resolveTurn = r })
      inFlightTurnRef.current = thisTurnPromise

      // Release the StrictMode/sub-100ms dedup guard NOW so subsequent
      // rapid sends can enter and queue behind the previous turn. Without
      // this release, the second send silently returns at the guard at the
      // top of sendMessage and never gets queued. The 60s content-dedup
      // guard above still prevents identical re-sends.
      sendInFlightRef.current = false

      // Optimistically render the user's message BEFORE awaiting the previous
      // turn so they see immediate feedback even if the queue makes them wait
      // for the previous assistant reply to finish.
      if (!hideUserMessage) {
        // displayContent: when set, the bubble + persisted history shows
        // the friendly label (e.g. "Use demo data") while the LLM still
        // sees `content` (the verbose analysis blob).
        const visibleContent = (options?.displayContent ?? content).trim()
        const optimisticUserMessage: ChatMessage = {
          id: `user-${Date.now()}`,
          role: 'user',
          content: visibleContent,
          timestamp: new Date(),
          attachments: options?.attachments?.length ? options.attachments : undefined,
        }
        if (isTargetConversationActive()) {
          setMessages((prev) => [...prev, optimisticUserMessage])
        }
        if (targetConversationId) {
          // Forward the staged attachments so the persisted user message
          // can carry filename/type/assetId metadata. Without this the
          // attachments column on chat_messages stays NULL and the agent
          // loses all knowledge of prior-turn file context (#1818-class
          // bug — agent re-asks user to upload a file they already sent).
          const persistAttachments = options?.attachments?.length
            ? options.attachments.map(a => ({
                filename: a.filename,
                fileType: a.fileType,
                assetId: a.assetId,
                url: a.url,
              }))
            : undefined
          saveConversationChatMessage(token, targetConversationId, {
            role: 'user',
            content: visibleContent,
            attachments: persistAttachments,
          }).catch(err => {
            console.warn('[useChat] Failed to save user message, retrying...', err)
            setTimeout(() => {
              saveConversationChatMessage(token, targetConversationId, {
                role: 'user',
                content: visibleContent,
                attachments: persistAttachments,
              }).catch(err2 => console.error('[useChat] Retry failed for user message:', err2))
            }, 2000)
          })
        }
      }

      // SERIALIZE turns. Wait for the previous assistant turn to fully finish
      // (stream + persist) before starting the new one, instead of aborting
      // mid-stream. previousTurn was captured synchronously at the top of
      // sendMessage so each send chains onto whatever was in flight at the
      // moment it entered.
      if (previousTurn && previousTurn !== thisTurnPromise) {
        try {
          await previousTurn
        } catch { /* previous turn errored; we proceed regardless */ }
      }

      // Noise reduction: if this is a module navigation command ("show calendar",
      // "open contacts") and the previous message pair was also a navigation,
      // replace the old pair instead of stacking them in the chat.
      const isNav = isModuleNavigationMessage(content)
      if (isNav && isTargetConversationActive()) {
        setMessages((prev) => {
          if (prev.length < 2) return prev
          const lastMsg = prev[prev.length - 1]
          const secondLast = prev[prev.length - 2]
          // Check if the last pair is user-nav → assistant-response (navigation noise)
          if (
            secondLast?.role === 'user'
            && lastMsg?.role === 'assistant'
            && isModuleNavigationMessage(secondLast.content)
          ) {
            // Remove the previous navigation pair
            return prev.slice(0, -2)
          }
          return prev
        })
        // Also clear any streaming message from the aborted previous request
        setStreamingMessage(null)
      }

      // Add user message immediately (optimistic update) - unless hidden
      // (User message was already optimistically rendered + persisted above
      //  before we awaited the previous turn — see the optimistic block at
      //  the top of sendMessage.)

      // Show thinking indicator IMMEDIATELY after user message (#1700).
      // This eliminates the 100-500ms gap before SSE start event arrives.
      // The real SSE status will replace this local indicator naturally.
      if (isTargetConversationActive() && !hideAssistantResponse) {
        setIsStreaming(true)
        clearActivity()
        pushActivity('Thinking...', 'thinking')
      }

      // Prepare chat request - send messages array to backend
      // Use profile.id for userId (not the session token)

      // queryMode controls routing (try deterministic first vs skip to agentic)
      // mode controls LLM provider (local vs claude)
      //
      // Core mode ('local'):
      //   queryMode='deterministic' → try conversation context first, then local LLM with tools
      // External mode ('api'):
      //   queryMode='tools' → skip deterministic, go straight to Claude with tools
      const queryMode = mode === 'api' ? 'tools' : 'deterministic'

      // For ephemeral sessions (custom systemPrompt), include full chat history
      // since server-side history lookup won't find these conversations
      const historyMessages: Array<{ role: 'user' | 'assistant'; content: string }> = [];
      if (systemPrompt) {
        for (const msg of messagesRef.current) {
          if ((msg.role === 'user' || msg.role === 'assistant') && msg.content) {
            historyMessages.push({ role: msg.role, content: msg.content });
          }
        }
      }

      // Read from refs so we always see the freshest page/object context even
      // if the prop hasn't flushed through a memoized callback yet (tab
      // switches and page-create races can otherwise drop these fields).
      const currentActivePageContext = activePageContextRef.current
      const currentActiveDriveFolder = activeDriveFolderRef.current
      const currentActiveObjectContext = activeObjectContextRef.current
      const currentFocusedSections = focusedSectionsRef.current

      // When a page is open, always populate activePageContext on the
      // outbound request and fold in any focused sections the inspector has
      // reported so the agent knows exactly what parts to edit.
      const outboundActivePageContext = currentActivePageContext
        ? {
            ...currentActivePageContext,
            ...(currentFocusedSections.length > 0
              ? { focusedSections: currentFocusedSections }
              : {}),
          }
        : undefined

      // NOTE: do NOT clear focusedSections here. We MUST keep the ref
      // populated for the duration of this turn's SSE stream so the
      // html.init rebuild guard at line ~2680 can see "user has focus
      // active" and refuse a stray full-page rebuild from the agent.
      // An earlier attempt to clear at send-time created a regression
      // (R6-CRITICAL-1 in round-2 review): the ref went empty the
      // moment we sent, so the guard's hasFocusedSections check
      // returned false during the stream and a leaked html.init would
      // wipe the page. The clear now happens in the SSE 'complete'
      // handler at the bottom of this loop, so the in-flight turn
      // stays defended end-to-end and turn 2 still starts fresh.

      const request: ChatRequest = {
        messages: [
          ...historyMessages,
          {
            role: 'user',
            content: content.trim(),
          },
        ],
        userId: profile?.id || undefined,
        guestId: token ? undefined : (guestId || undefined),
        conversationId: targetConversationId,
        workspaceId,
        activeModuleId,
        activeModuleObjectType,
        activeModuleLabel,
        activePageContext: outboundActivePageContext,
        activeDriveFolder: currentActiveDriveFolder ?? undefined,
        activeObjectContext: currentActiveObjectContext || undefined,
        mode,
        model,  // Pass specific model if provided
        queryMode,
        // Silent one-off turns (hideAssistantResponse — currently only the Daily
        // Brief quick-capture) run the agent + tools but must leave NO trace in
        // the chat thread: don't read prior history, don't persist any message.
        skipHistory: hideAssistantResponse ? true : undefined,
        skipPersistence: hideAssistantResponse ? true : undefined,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        voiceMode: options?.voiceMode ? true : undefined,
        // Force BOOTSTRAP + Sonnet when the user hasn't completed onboarding,
        // regardless of conversation title. Fixes the case where a user clicks
        // "New chat" and gets vanilla agent behavior mid-onboarding.
        isOnboarding: !isOnboardingComplete(profileRef.current?.onboardingState) ? true : undefined,
        // Pass the canonical onboarding state so x-agent can tailor its system
        // prompt to the current step (see prompt-assembler.ts).
        // Uses profileRef to always read the latest state — the closure can be
        // stale when handleGmailSyncComplete calls sendMessage after refreshSession().
        onboardingState: profileRef.current?.onboardingState,
        // Pass pre-fetched context to avoid slow tool calls
        context: (() => {
          const base = context || {}
          if (options?.attachments?.length) {
            const fileLines = options.attachments.map(att => {
              const urlPart = att.url ? ` — URL: ${att.url}` : ''
              const idPart = att.assetId ? ` (asset ID: ${att.assetId})` : ''
              return `• ${att.filename} (${att.fileType})${idPart}${urlPart}`
            }).join('\n')
            return { ...base, attachedFiles: `The user has attached the following files:\n${fileLines}` }
          }
          return context || undefined
        })(),
        // Custom system prompt (e.g., for personalization mode)
        systemPrompt: systemPrompt || undefined,
        // Restrict available tools (e.g., ['save_memory'] for personalization)
        allowedTools: allowedTools || undefined,
        // Image attachments for vision (base64 encoded)
        imageAttachments: options?.images && options.images.length > 0 ? options.images : undefined,
        // Document attachments (PDFs) as base64
        documentAttachments: options?.documents && options.documents.length > 0 ? options.documents : undefined,
        // Attachment metadata for persistence (survives page refresh)
        attachments: options?.attachments?.length ? options.attachments.map(att => ({
          filename: att.filename,
          fileType: att.fileType,
          assetId: att.assetId,
          url: att.url,
        })) : undefined,
        // Large-paste content chips, forwarded as a structured field so the
        // server can place them in the attachment block of the system prompt
        // rather than inlining into the user turn (see paste guard in
        // ChatBox.tsx). Prevents >4KB HTML blocks from blowing the context
        // budget when the user just wants to discuss the paste.
        pastedAttachments: options?.pastedAttachments?.length ? options.pastedAttachments : undefined,
      }

      // Turn-stable values for THIS send (NOT reassigned per-event). These are
      // shared mutable containers (arrays / registry) the cores push into, plus
      // the request envelope + the assistant message id. They flow to the cores
      // via `deps`, distinct from the 16 reassigned turn-local primitives + 2
      // Maps which live on `ctx` (see useChatStream.types.ts).
      const requestStartTime = Date.now()
      const assistantThinking = ''
      const assistantId = `assistant-${Date.now()}`
      const toolCalls: Array<{ name: string; params: any }> = []
      const toolResults: Array<Record<string, unknown>> = []
      // Track agent create_object calls by tool_use id so the matching
      // tool_result/tool_receipt can fire a targeted list-query invalidation.
      // Solves #2458: background enrichment (Google Places + R2 photo upload,
      // ~2-5s) lands AFTER the generic 500ms onToolActivity invalidation reads
      // the cache, so the user keeps seeing the unenriched snapshot for up to
      // staleTime (30s). We re-invalidate at the receipt moment AND with a
      // short follow-up (ENRICHMENT_FOLLOWUP_MS) so enrichment is picked up
      // promptly.
      //
      // Why id-keyed (not FIFO): two parallel `create_object` tool_uses can
      // return results out of order, or one can be retried. A FIFO array
      // mis-correlates the receipt to the wrong type and invalidates the
      // wrong list cache. The toolUseId is forwarded by agent-loop.ts
      // (originating from the model's tool_use block); falls back to a
      // synthetic ordinal key when older agents omit it.
      const pendingCreates = createPendingCreateRegistry()
      const queuedUiActions: ChatUIAction[] = []
      const queuedQuickActions: QuickAction[] = []

      // Per-turn mutable context: the 16 reassigned turn-local primitives + the
      // 2 module-registry Maps the SSE loop (core A → runChatStream) and the
      // ui-action switch (core B → applyUiDirective) share. Created fresh per
      // send and passed BY REFERENCE so every `ctx.<field> = …` write is
      // observed across both cores + the catch/finally — exactly as the inline
      // closure variables were (useChat.ts L667-L718, pre-decomposition).
      const ctx = createTurnContext()

      // When the agent attempts a full rebuild (html.init) on the currently
      // active page but the user's message does NOT carry explicit rebuild
      // Note: rebuild-intent guard moved to server (x-agent/src/routes/chat.ts).
      // Server-side guard emits tool_result so the agent's next iteration can
      // retry with html.patch. The client-side guard can't deliver that
      // feedback (SSE has already started by the time client decides). Removed.

      // Stable dependency bundle the cores read but never reassign on ctx.
      // Built once per send; refs/setters carry stable identities. The
      // markInterleaved/markXaviorStatus helpers close over `ctx` here so the
      // report/html branches in applyUiDirective mutate the SAME ctx flags as
      // runChatStream's loop body.
      const deps: ChatStreamDeps = {
        content,
        request,
        targetConversationId,
        requestStartTime,
        assistantId,
        assistantThinking,
        hideAssistantResponse,
        toolCalls,
        toolResults,
        queuedUiActions,
        queuedQuickActions,
        pendingCreates,
        isTargetConversationActive,
        markInterleavedActivity: () => {
          ctx.sawInterleavedActivitySinceLastText = true
        },
        markXaviorStatusActivity: () => {
          ctx.sawInterleavedActivitySinceLastText = true
          ctx.sawXaviorStatusSinceLastText = true
          ctx.lastXaviorStatusAtMs = Date.now()
        },
        token,
        workspaceId,
        mode,
        queryClient,
        // The inline ui-action (report/html) branch read the closure-captured
        // PROP `activePageContext` (NOT the fresh `currentActivePageContext`
        // ref snapshot, which the surrounding html-chunk/patch dispatch-id
        // canonicalization used via activePageContextRef.current). Preserve that
        // exact distinction: pass the prop here, the ref below.
        activePageContext,
        activePageContextRef,
        profileRef,
        focusedSectionsRef,
        lastSentContentRef,
        lastSendRef,
        lastStreamEventAtRef,
        abortControllerRef,
        streamActivityRef,
        streamingContentRef,
        sendInFlightRef,
        // Silent turns (e.g. the Daily Summary quick-capture) must touch NONE of
        // the chat-visible UI — no thinking shimmer, no activity timeline, no
        // streaming bubble, no error/loading. Pass no-op display setters; the
        // agent still runs and create_object invalidations/silent-response fire
        // through the non-UI paths. (setMessages stays real but is internally
        // gated by hideAssistantResponse, so nothing is appended.)
        setThinking: hideAssistantResponse ? () => {} : setThinking,
        setIsStreaming: hideAssistantResponse ? () => {} : setIsStreaming,
        setIsLoading: hideAssistantResponse ? () => {} : setIsLoading,
        setMessages,
        setStreamingMessage: hideAssistantResponse ? () => {} : setStreamingMessage,
        setError: hideAssistantResponse ? () => {} : setError,
        setRetryBubble: hideAssistantResponse ? () => {} : setRetryBubble,
        setPendingQuickActions: hideAssistantResponse ? () => {} : setPendingQuickActions,
        setFocusedSections: hideAssistantResponse ? () => {} : setFocusedSections,
        pushActivity: hideAssistantResponse ? () => {} : pushActivity,
        pushThinkingStatus: hideAssistantResponse ? () => {} : pushThinkingStatus,
        clearActivity: hideAssistantResponse ? () => {} : clearActivity,
        onMessage,
        onQuickAck,
        onToolActivity,
        onObjectCreated,
        onStreamTextChunk,
        onError,
        onComplete,
        onUiActions,
      }

      try {
        // Core (A) — SSE loop + finalizeAssistantMessage + error/abort recovery.
        // Mutates ctx by reference; delegates the ui-action switch to core (B)
        // via applyUiDirective. The inner (core-A) finally resets streaming UI
        // state + the abort controller; the outer finally below owns the
        // send-queue bookkeeping, so ordering matches the inline try/finally.
        await runChatStream(ctx, deps)
      } finally {
        // Resolve the in-flight turn promise so any queued sendMessage call
        // waiting on it can proceed. Clear the ref only if it still points
        // at THIS turn (a later send may have already replaced it).
        resolveTurn?.()
        if (inFlightTurnRef.current === thisTurnPromise) inFlightTurnRef.current = null
        // Always signal stream end (success, error, or abort) for final data refresh
        onStreamEnd?.()
      }
    },
    [token, profile, guestId, sessionId, workspaceId, mode, model, systemPrompt, allowedTools, onMessage, onToolActivity, onObjectCreated, onStreamTextChunk, onStreamEnd, onThinking, onError, onComplete, onUiActions, onQuickAck, pushActivity, clearActivity]
  )


  /**
   * Retry the last sent message after a transient error (#1696).
   * Clears the dedup guard so the same content can be re-sent.
   */
  const retryLastMessage = useCallback(() => {
    // Prefer the retry bubble's explicit prompt (layer-3 empty-response
    // retry) so we re-send exactly what the user originally typed for
    // that failed turn. Fall back to the generic last-content ref for
    // transient errors that didn't produce a retry bubble.
    const content = retryBubble?.retryPrompt || lastSentContentRef.current
    if (!content) return
    // Clear the retry bubble so it doesn't stick around after the resend
    // starts, and clear the dedup guard so the retry isn't blocked.
    setRetryBubble(null)
    lastSendRef.current = null
    setError(null)
    void sendMessage(content)
  }, [retryBubble, sendMessage])

  /**
   * Add local messages for deterministic responses (bypasses LLM streaming)
   */
  const addLocalMessages = useCallback(
    (userContent: string, assistantContent: string, conversationId?: string, metadata?: {
      routing?: string
      durationMs?: number
      intent?: string
    }) => {
      const now = Date.now()
      const messagesToAdd: ChatMessage[] = []
      const userMessageId = userContent ? `user-${now}` : undefined
      const assistantMessageId = `assistant-${now}`

      // Only add user message if content is non-empty
      if (userContent && userMessageId) {
        const userMessage: ChatMessage = {
          id: userMessageId,
          role: 'user',
          content: userContent,
          timestamp: new Date(),
        }
        messagesToAdd.push(userMessage)
      }

      // Add assistant message with deterministic metadata
      const assistantMessage: ChatMessage = {
        id: assistantMessageId,
        role: 'assistant',
        content: assistantContent,
        timestamp: new Date(),
        routing: metadata?.routing || 'deterministic',
        durationMs: metadata?.durationMs || 0,
        mode: 'local', // Deterministic is always "local"
      }
      messagesToAdd.push(assistantMessage)

      // Noise reduction: if this is a view/navigation command and the previous
      // message pair was also a view command, replace it instead of stacking.
      // Navigation commands are ephemeral — only the most recent one matters.
      const isViewCommand = metadata?.routing?.startsWith('command:view')
      setMessages((prev) => {
        if (isViewCommand) {
          // Remove ALL consecutive trailing view-command pairs (not just the last one)
          let trimmed = prev
          while (trimmed.length >= 2) {
            const last = trimmed[trimmed.length - 1]
            const secondLast = trimmed[trimmed.length - 2]
            if (
              secondLast?.role === 'user'
              && last?.role === 'assistant'
              && last.routing?.startsWith('command:view')
            ) {
              trimmed = trimmed.slice(0, -2)
            } else {
              break
            }
          }
          return [...trimmed, ...messagesToAdd]
        }
        return [...prev, ...messagesToAdd]
      })

      // Save messages to server in strict order (user first, then assistant)
      // so deterministic local echoes never reorder after reload/switch.
      // Skip saving view navigation commands — they are ephemeral UI actions.
      if (conversationId && !isViewCommand) {
        void (async () => {
          if (userContent) {
            try {
              await saveConversationChatMessage(token, conversationId, {
                role: 'user',
                content: userContent,
              })
            } catch (err) {
              console.error('[useChat] Failed to save user message:', err)
            }
          }

          try {
            await saveConversationChatMessage(token, conversationId, {
              role: 'assistant',
              content: assistantContent,
              mode: 'local',
              routing: metadata?.routing || 'deterministic',
              durationMs: metadata?.durationMs,
            })
          } catch (err) {
            console.error('[useChat] Failed to save assistant message:', err)
          }
        })()
      }

      // Trigger onMessage callback
      onMessage?.(assistantMessage)

      return {
        userMessageId,
        assistantMessageId,
      }
    },
    [token, onMessage]
  )

  const addAssistantMessage = useCallback(
    (assistantContent: string, conversationId?: string, metadata?: {
      routing?: string
      durationMs?: number
      intent?: string
    }) => {
      const now = Date.now()
      const assistantMessage: ChatMessage = {
        id: `assistant-${now}`,
        role: 'assistant',
        content: assistantContent,
        timestamp: new Date(),
        routing: metadata?.routing || 'deterministic',
        durationMs: metadata?.durationMs || 0,
        mode: 'local',
      }

      setMessages((prev) => [...prev, assistantMessage])

      if (conversationId) {
        saveConversationChatMessage(token, conversationId, {
          role: 'assistant',
          content: assistantContent,
          mode: 'local',
          routing: metadata?.routing || 'deterministic',
          durationMs: metadata?.durationMs,
        }).catch(err => console.error('[useChat] Failed to save assistant message:', err))
      }

      onMessage?.(assistantMessage)
    },
    [token, onMessage]
  )

  const clearMessages = useCallback(async () => {
    setMessages([])
    setThinking(null)
    setError(null)
    setStreamingMessage(null)
    // Clear session on server (starts fresh)
    if (sessionId) {
      try {
        await clearChatSession(token, sessionId)
      } catch (err) {
        console.error('[useChat] Failed to clear chat session:', err)
      }
    }
  }, [sessionId, token])

  const stopStreaming = useCallback(() => {
    // Abort any active fetch requests (chat messages)
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
      abortControllerRef.current = null
    }
    // Abort greeting HTTP request to stop LLM generation
    if (greetingAbortControllerRef.current) {
      greetingAbortControllerRef.current.abort()
      greetingAbortControllerRef.current = null
    }
    // Cancel any in-progress greeting
    greetingCancelledRef.current = true
    // Always clear streaming state (works for both messages and greetings)
    setIsStreaming(false)
    setIsLoading(false)
    setThinking(null)
    setStreamingMessage(null)
  }, [])

  // requestGreeting extracted verbatim into useChatGreeting (Stage 3). The
  // greeting refs stay in this orchestrator (the session-switch effect,
  // sendMessage, and stopStreaming all read/write them) and are threaded in
  // by reference. The useCallback + dep array are byte-identical and this hook
  // call sits at the SAME body position so React hook order is unchanged.
  const { requestGreeting } = useChatGreeting({
    token,
    workspaceId,
    isStreaming,
    streamingMessage,
    setStreamingMessage,
    setIsStreaming,
    setPendingQuickActions,
    currentSessionIdRef,
    greetingInProgressRef,
    greetingLastRequestedRef,
    greetingCancelledRef,
    greetingAbortControllerRef,
  })

  // Calculate last message time for inactivity detection
  const lastMessageTime = messages.length > 0
    ? messages[messages.length - 1].timestamp
    : null

  // Combine messages with streaming message for display.
  // Skip appending streamingMessage if a greeting with the same ID or content was already
  // added to messages[] (e.g. via prepend-on-send or server fetch).
  // ID check: catches the case where streamingMessage was prepended to messages[] directly.
  // Content check: catches the case where the persisted greeting was re-fetched from server.
  const greetingAlreadyInMessages =
    streamingMessage?.routing === 'greeting' &&
    !streamingMessage.streaming &&
    messages.some(m =>
      m.role === 'assistant' &&
      (m.id === streamingMessage.id || m.content === streamingMessage.content)
    )
  // Build displayMessages and deduplicate by ID as a safety net against
  // intermediate React batching states that can produce same-key duplicates.
  // Append any transient retry bubble (layer 3 of the "No response"
  // recovery) so the UI can render its retry affordance — the bubble is
  // never persisted, so it is only ever read out of the `retryBubble`
  // state here, not out of `messages`.
  const displayMessagesRaw =
    streamingMessage && !greetingAlreadyInMessages
      ? [...messages, streamingMessage]
      : messages
  const displayMessagesWithRetry = retryBubble
    ? [...displayMessagesRaw, retryBubble]
    : displayMessagesRaw
  const seenIds = new Set<string>()
  const displayMessages = displayMessagesWithRetry.filter(m => {
    if (seenIds.has(m.id)) return false
    seenIds.add(m.id)
    return true
  })

  const dismissRetryBubble = useCallback(() => {
    setRetryBubble(null)
  }, [])

  // Imperatively append result-aware follow-up chips (e.g. after a client-side
  // CSV import succeeds, #2014). Deduped by label against the existing pending
  // chips so re-emitting the same suggestions can't stack duplicates.
  const addQuickActions = useCallback((actions: QuickAction[]) => {
    setPendingQuickActions((prev) => appendQuickActions(prev, actions))
  }, [])

  return {
    messages: displayMessages,
    thinking,
    streamActivity,
    isStreaming,
    isLoading,
    error,
    elapsedSeconds,
    sendMessage,
    retryLastMessage,
    addLocalMessages,
    addAssistantMessage,
    appendUserMessage: setMessages,
    clearMessages,
    stopStreaming,
    requestGreeting,
    lastMessageTime,
    pendingQuickActions,
    clearPendingQuickActions: () => setPendingQuickActions([]),
    addQuickActions,
    retryBubble,
    dismissRetryBubble,
  }
}
